import React, { useState, useEffect } from 'react';
import { Container, Card, Alert, Spinner, Badge, Tabs, Tab, Button } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { generateSlug } from '../utils/slugUtils';
import { useSavedRestaurantIds } from '../hooks/useSavedRestaurantIds';
import RestaurantCard from './RestaurantCard';
import VisitReviewGroupCard from './VisitReviewGroupCard';
import { groupReviewsByVisit, reviewDishName } from '../utils/reviewGrouping';
import DishPhoto from './DishPhoto';
import { groupPhotosByDish, visitDishLabel } from '../utils/visitPhotos';

function UserProfilePage() {
  const { userId } = useParams();
  const { token, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  // Saved-state for the shared RestaurantCard badges (#61/#67). One hook call
  // for the whole page — the card takes these Sets as props.
  const { favoriteIds, wishlistIds } = useSavedRestaurantIds();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('restaurants');

  // Follow state
  const [isFollowing, setIsFollowing] = useState(false);
  const [followStatus, setFollowStatus] = useState(null); // 'pending', 'accepted', or null
  const [followLoading, setFollowLoading] = useState(false);

  // Block state
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);

  // User's content
  const [userRestaurants, setUserRestaurants] = useState([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(false);
  const [userReviews, setUserReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [userFavorites, setUserFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [userWishlist, setUserWishlist] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [userVisits, setUserVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    fetchUserProfile();
    if (isAuthenticated && userId) {
      checkFollowStatus();
      checkBlockStatus();
    }
  }, [userId, isAuthenticated]);

  // Fetch data for the active tab when profile loads
  useEffect(() => {
    if (!profile || profile.is_private) return;

    if (activeTab === 'restaurants') {
      fetchUserRestaurants();
    } else if (activeTab === 'reviews') {
      fetchUserReviews();
    } else if (activeTab === 'favorites') {
      fetchUserFavorites();
    } else if (activeTab === 'wishlist') {
      fetchUserWishlist();
    } else if (activeTab === 'visits') {
      fetchUserVisits();
    }
  }, [profile]);

  const fetchUserProfile = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/users/social/profile/${userId}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const data = await response.json();
      setProfile(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkFollowStatus = async () => {
    if (!token || !userId) return;

    try {
      // Check if we're following this user by checking our following list
      const response = await fetch(`${API_BASE_URL}/users/social/following`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const following = await response.json();
        const followRelation = following.find(f => f.following_id === parseInt(userId));
        if (followRelation) {
          setIsFollowing(true);
          setFollowStatus(followRelation.status);
        } else {
          setIsFollowing(false);
          setFollowStatus(null);
        }
      }
    } catch (err) {
      console.error('Error checking follow status:', err);
    }
  };

  const checkBlockStatus = async () => {
    if (!token || !userId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/users/blocks/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const blocks = await response.json();
        setIsBlocked(blocks.some(b => b.blocked_user_id === parseInt(userId)));
      }
    } catch (err) {
      console.error('Error checking block status:', err);
    }
  };

  const handleBlock = async () => {
    if (!window.confirm('Block this user? You will no longer see their reviews or content.')) {
      return;
    }
    setBlockLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/blocks/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to block user');
      }
      setIsBlocked(true);
      setSuccess('User blocked. Their content is now hidden from you.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBlockLoading(false);
    }
  };

  const handleUnblock = async () => {
    setBlockLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/blocks/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to unblock user');
      }
      setIsBlocked(false);
      setSuccess('User unblocked.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBlockLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setFollowLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/follow/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to follow user');
      }

      const data = await response.json();
      setIsFollowing(true);
      setFollowStatus(data.status);

      // Show appropriate message
      if (data.status === 'pending') {
        setError(''); // Clear any errors
        setSuccess('Follow request sent! Waiting for approval.');
      } else {
        setError('');
        setSuccess('Now following this user!');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleUnfollow = async () => {
    setFollowLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/follow/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to unfollow user');
      }

      setIsFollowing(false);
      setFollowStatus(null);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setFollowLoading(false);
    }
  };

  const fetchUserRestaurants = async () => {
    if (userRestaurants.length > 0 || !profile || profile.is_private) return;

    setRestaurantsLoading(true);
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/restaurants/?created_by_user_id=${userId}`, {
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setUserRestaurants(data);
      }
    } catch (err) {
      console.error('Error fetching restaurants:', err);
    } finally {
      setRestaurantsLoading(false);
    }
  };

  const fetchUserReviews = async () => {
    if (userReviews.length > 0 || !profile || profile.is_private) return;

    setReviewsLoading(true);
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/reviews/?user_id=${userId}`, {
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setUserReviews(data);
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  };

  const fetchUserFavorites = async () => {
    if (userFavorites.length > 0 || !profile || profile.is_private) return;

    setFavoritesLoading(true);
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/favorites/?user_id=${userId}`, {
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setUserFavorites(data);
      } else {
        console.error('Failed to fetch favorites, status:', response.status);
      }
    } catch (err) {
      console.error('Error fetching favorites:', err);
    } finally {
      setFavoritesLoading(false);
    }
  };

  const fetchUserVisits = async () => {
    if (userVisits.length > 0 || !profile || profile.is_private) return;

    setVisitsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/visits/public/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setUserVisits(data.visits || []);
      }
    } catch (err) {
      console.error('Error fetching visits:', err);
    } finally {
      setVisitsLoading(false);
    }
  };

  const fetchUserWishlist = async () => {
    if (userWishlist.length > 0 || !profile || profile.is_private) return;

    setWishlistLoading(true);
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/wishlist/?user_id=${userId}`, {
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setUserWishlist(data);
      } else {
        console.error('Failed to fetch wishlist, status:', response.status);
      }
    } catch (err) {
      console.error('Error fetching wishlist:', err);
    } finally {
      setWishlistLoading(false);
    }
  };

  // A saved dish (#102) — favorites/wishlist can now target a catalog Food, not
  // just a restaurant. Renders a compact card linking to the food's page.
  const renderFoodSaveCard = (save) => (
    <div key={`food-${save.id}`} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-3">
      <Card
        className="h-100 shadow-sm"
        style={{ cursor: 'pointer' }}
        onClick={() => navigate(`/food/${save.food_id}`)}
      >
        {save.food_image_url && (
          <Card.Img
            variant="top"
            src={save.food_image_url}
            alt={save.food_name || 'dish'}
            style={{ height: '140px', objectFit: 'cover' }}
          />
        )}
        <Card.Body>
          <Card.Title className="h6 mb-1">🍽️ {save.food_name || 'Dish'}</Card.Title>
          {save.notes && <Card.Text className="small text-muted mb-0">{save.notes}</Card.Text>}
        </Card.Body>
      </Card>
    </div>
  );

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'restaurants') {
      fetchUserRestaurants();
    } else if (tab === 'reviews') {
      fetchUserReviews();
    } else if (tab === 'favorites') {
      fetchUserFavorites();
    } else if (tab === 'wishlist') {
      fetchUserWishlist();
    } else if (tab === 'visits') {
      fetchUserVisits();
    }
  };

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading profile...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container className="my-5">
        <Alert variant="warning">User not found</Alert>
      </Container>
    );
  }

  // Check if viewing own profile
  const isOwnProfile = user && user.id === parseInt(userId);

  if (isOwnProfile) {
    // Redirect to own profile page
    navigate('/profile');
    return null;
  }

  // Private profile view
  if (profile.is_private) {
    return (
      <Container className="my-5">
        <Card>
          <Card.Body className="text-center py-5">
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔒</div>
            <h3>Private Profile</h3>
            <p className="text-muted">
              This user's profile is private and cannot be viewed.
            </p>
            {profile.display_name && (
              <p className="mt-3">
                <strong>{profile.display_name}</strong>
                {profile.is_verified && (
                  <Badge bg="success" className="ms-2">✓ Verified</Badge>
                )}
              </p>
            )}
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Public profile view
  return (
    <Container className="my-5">
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex align-items-start gap-3">
            {profile.avatar_url && (
              <img
                src={profile.avatar_url}
                alt={profile.display_name || 'User avatar'}
                style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
              />
            )}
            <div className="flex-grow-1">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h2 className="mb-2">
                    {profile.display_name || profile.username || `User ${profile.user_id}`}
                    {profile.is_verified && (
                      <Badge bg="success" className="ms-2">✓ Verified</Badge>
                    )}
                  </h2>
                  {profile.location && (
                    <p className="text-muted mb-2">
                      📍 {profile.location}
                    </p>
                  )}
                  {profile.bio && (
                    <p className="mb-2">{profile.bio}</p>
                  )}
                  {profile.website_url && (
                    <p className="mb-0">
                      <a href={profile.website_url} target="_blank" rel="noopener noreferrer">
                        🌐 {profile.website_url}
                      </a>
                    </p>
                  )}
                </div>
                {isAuthenticated && !isOwnProfile && (
                  <div className="d-flex flex-column align-items-end gap-2">
                    {isFollowing ? (
                      <div>
                        <Button
                          variant="outline-secondary"
                          onClick={handleUnfollow}
                          disabled={followLoading}
                        >
                          {followLoading ? 'Unfollowing...' : followStatus === 'pending' ? 'Request Pending' : 'Following'}
                        </Button>
                        {followStatus === 'pending' && (
                          <div className="text-muted small mt-1">Waiting for approval</div>
                        )}
                      </div>
                    ) : (
                      <Button
                        variant="primary"
                        onClick={handleFollow}
                        disabled={followLoading}
                      >
                        {followLoading ? 'Following...' : 'Follow'}
                      </Button>
                    )}
                    {isBlocked ? (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleUnblock}
                        disabled={blockLoading}
                      >
                        {blockLoading ? 'Unblocking...' : 'Unblock User'}
                      </Button>
                    ) : (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleBlock}
                        disabled={blockLoading}
                      >
                        {blockLoading ? 'Blocking...' : 'Block User'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-3">
            <Tab eventKey="restaurants" title="Restaurants">
              {restaurantsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                  <p className="mt-2">Loading restaurants...</p>
                </div>
              ) : userRestaurants.length > 0 ? (
                <div>
                  <p className="text-muted mb-3">Restaurants added by this user</p>
                  <div className="row">
                    {[...userRestaurants]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((restaurant) => (
                        <div key={restaurant.id} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-3">
                          <RestaurantCard
                            restaurant={restaurant}
                            favoriteIds={favoriteIds}
                            wishlistIds={wishlistIds}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">No restaurants added yet</p>
                </div>
              )}
            </Tab>

            <Tab eventKey="reviews" title="Reviews">
              {reviewsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                  <p className="mt-2">Loading reviews...</p>
                </div>
              ) : userReviews.length > 0 ? (
                <div>
                  <p className="text-muted mb-3">Reviews by this user</p>
                  {(() => {
                    // #116: sort newest-first, then group by VISIT (not by
                    // restaurant). A multi-dish public visit fans out to one review
                    // per rated dish; grouping by visit collapses it back to a single
                    // card — same as the owner's own My Reviews and the restaurant
                    // Reviews list — instead of N "Visit Review" cards repeating the
                    // one visit note with no dish name/photo (the bug this fixes).
                    const sortedReviews = [...userReviews].sort((a, b) => {
                      const dateA = new Date(a.visit_date || a.created_at);
                      const dateB = new Date(b.visit_date || b.created_at);
                      return dateB - dateA;
                    });

                    return groupReviewsByVisit(sortedReviews).map((group) => {
                      // Multi-dish visit → the shared grouped card (read-only here;
                      // the public profile has no report wiring today).
                      if (group.length > 1) {
                        return <VisitReviewGroupCard key={`visit-${group[0].visit_id}`} group={group} />;
                      }

                      // Singleton: a standalone review or a single-dish visit. Show
                      // the dish/food/restaurant name (never a "Visit Review"
                      // fallback), its type, the note, the user's own visit photos
                      // (#115) — not the restaurant's catalog images — and the date.
                      const review = group[0];
                      let reviewedType = null;
                      let reviewedId = null;
                      let reviewedItem = null;
                      let reviewTypeIcon = '⭐';
                      let reviewTypeLabel = '';

                      if (review.menu_item_id || review.dish_name) {
                        reviewedType = 'food';
                        reviewedId = review.menu_item?.food?.id || review.food_id;
                        reviewedItem = reviewDishName(review) || 'Dish';
                        reviewTypeIcon = '🍴';
                        reviewTypeLabel = review.restaurant?.name ? `Dish at ${review.restaurant.name}` : 'Dish';
                      } else if (review.food_id) {
                        reviewedType = 'food';
                        reviewedId = review.food_id;
                        reviewedItem = review.food?.name || 'Food Item';
                        reviewTypeIcon = '🍽️';
                        reviewTypeLabel = 'Food';
                      } else if (review.restaurant_id) {
                        reviewedType = 'restaurant';
                        reviewedId = review.restaurant_id;
                        reviewedItem = review.restaurant?.name || 'Restaurant';
                        reviewTypeIcon = '🏪';
                        reviewTypeLabel = 'Restaurant';
                      } else {
                        reviewedItem = 'Review';
                      }

                      const canNavigate = reviewedType && reviewedId;

                      return (
                        <Card
                          key={review.id}
                          className="mb-3 shadow-sm"
                          style={canNavigate ? { cursor: 'pointer' } : undefined}
                          onClick={() => {
                            if (reviewedType === 'restaurant' && reviewedId) {
                              navigate(`/restaurant/${reviewedId}/${generateSlug(reviewedItem)}`);
                            } else if (reviewedType === 'food' && reviewedId) {
                              navigate(`/food/${reviewedId}/${generateSlug(reviewedItem)}`);
                            }
                          }}
                        >
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <div className="flex-grow-1 me-2">
                                <h6 className="mb-0">{reviewedItem}</h6>
                                <small className="text-muted">{reviewTypeIcon} {reviewTypeLabel}</small>
                              </div>
                              <Badge bg="primary" style={{ flexShrink: 0 }}>★ {review.rating}/5</Badge>
                            </div>

                            {review.title && <p className="mb-1 small fw-semibold">{review.title}</p>}
                            {review.review_text && (
                              <p className="mb-2 small" style={{ whiteSpace: 'pre-line' }}>{review.review_text}</p>
                            )}

                            {review.photos && review.photos.length > 0 && (
                              <div className="d-flex flex-wrap gap-2 mb-2">
                                {review.photos.map((photo) => (
                                  <img
                                    key={photo.id}
                                    src={photo.url}
                                    alt={reviewedItem}
                                    onClick={(e) => { e.stopPropagation(); window.open(photo.url, '_blank', 'noopener'); }}
                                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer', border: '1px solid #dee2e6' }}
                                  />
                                ))}
                              </div>
                            )}

                            <div className="text-muted small">
                              {new Date(review.visit_date || review.created_at).toLocaleDateString()}
                            </div>
                          </Card.Body>
                        </Card>
                      );
                    });
                  })()}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">No reviews yet</p>
                </div>
              )}
            </Tab>

            <Tab eventKey="favorites" title="Favorites">
              {favoritesLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                  <p className="mt-2">Loading favorites...</p>
                </div>
              ) : userFavorites.length > 0 ? (
                <div>
                  {userFavorites.some((f) => f.restaurant) && (
                    <>
                      <p className="text-muted mb-3">Favorite restaurants</p>
                      <div className="row">
                        {[...userFavorites]
                          .filter((f) => f.restaurant)
                          .sort((a, b) => (a.restaurant?.name || '').localeCompare(b.restaurant?.name || ''))
                          .map((favorite) => (
                            <div key={favorite.id} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-3">
                              <RestaurantCard
                                restaurant={favorite.restaurant}
                                favoriteIds={favoriteIds}
                                wishlistIds={wishlistIds}
                              />
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                  {/* Favorite dishes (#102) */}
                  {userFavorites.some((f) => f.food_id) && (
                    <>
                      <p className="text-muted mb-3 mt-2">Favorite dishes</p>
                      <div className="row">
                        {[...userFavorites]
                          .filter((f) => f.food_id)
                          .sort((a, b) => (a.food_name || '').localeCompare(b.food_name || ''))
                          .map((fav) => renderFoodSaveCard(fav))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">No favorites yet</p>
                </div>
              )}
            </Tab>

            <Tab eventKey="wishlist" title="Wishlist">
              {wishlistLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                  <p className="mt-2">Loading wishlist...</p>
                </div>
              ) : userWishlist.length > 0 ? (
                <div>
                  {userWishlist.some((i) => i.restaurant) && (
                    <>
                      <p className="text-muted mb-3">Restaurants on wishlist</p>
                      <div className="row">
                        {[...userWishlist]
                          .filter((i) => i.restaurant)
                          .sort((a, b) => (a.restaurant?.name || '').localeCompare(b.restaurant?.name || ''))
                          .map((item) => (
                            <div key={item.id} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-3">
                              <RestaurantCard
                                restaurant={item.restaurant}
                                favoriteIds={favoriteIds}
                                wishlistIds={wishlistIds}
                                note={item.notes}
                              />
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                  {/* Dishes on wishlist (#102) */}
                  {userWishlist.some((i) => i.food_id) && (
                    <>
                      <p className="text-muted mb-3 mt-2">Dishes on wishlist</p>
                      <div className="row">
                        {[...userWishlist]
                          .filter((i) => i.food_id)
                          .sort((a, b) => (a.food_name || '').localeCompare(b.food_name || ''))
                          .map((item) => renderFoodSaveCard(item))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">No restaurants in wishlist yet</p>
                </div>
              )}
            </Tab>
            <Tab eventKey="visits" title="🍽️ Visits">
              {visitsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                  <p className="mt-2">Loading visits...</p>
                </div>
              ) : userVisits.length > 0 ? (
                (() => {
                  const visitsByRestaurant = userVisits.reduce((acc, visit) => {
                    const key = visit.restaurant_id;
                    if (!acc[key]) {
                      acc[key] = {
                        restaurantId: visit.restaurant_id,
                        restaurantName: visit.restaurant_name || `Restaurant ${visit.restaurant_id}`,
                        visits: []
                      };
                    }
                    acc[key].visits.push(visit);
                    return acc;
                  }, {});

                  Object.values(visitsByRestaurant).forEach(group => {
                    group.visits.sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date));
                  });

                  const sortedGroups = Object.values(visitsByRestaurant).sort((a, b) =>
                    new Date(b.visits[0].visit_date) - new Date(a.visits[0].visit_date)
                  );

                  return sortedGroups.map((group) => (
                    <div key={group.restaurantId} className="mb-4">
                      <h5
                        className="mb-3 pb-2 border-bottom"
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/restaurant/${group.restaurantId}/${generateSlug(group.restaurantName)}`)}
                      >
                        <span className="text-primary">{group.restaurantName}</span>
                        <Badge bg="secondary" className="ms-2 small">
                          {group.visits.length} visit{group.visits.length !== 1 ? 's' : ''}
                        </Badge>
                      </h5>

                      {group.visits.map((visit) => {
                        // #118: a multi-dish visit (#110) must show ALL its dishes,
                        // each with its own rating + #115 per-dish photos — the way
                        // the grouped review card does. Fall back to the singular
                        // back-compat fields only if `dishes` is somehow empty.
                        const dishes = (visit.dishes && visit.dishes.length > 0)
                          ? visit.dishes
                          : ((visit.food_name || visit.dish_name || visit.menu_item_name)
                              ? [{ id: `primary-${visit.id}`, food_name: visit.food_name, dish_name: visit.dish_name, menu_item_name: visit.menu_item_name, rating: null }]
                              : []);
                        // #230: the grouping and the label now live in utils/visitPhotos
                        // so the own-profile view (UserProfile.js) uses the SAME logic
                        // instead of a second hand-written copy that never arrived.
                        const dishLabel = visitDishLabel;
                        const { photosByDish, loosePhotos } = groupPhotosByDish(visit.photos);
                        const thumb = (photo, alt, visitDishId = null) => (
                          <DishPhoto
                            key={`${photo.id}-${visitDishId ?? 'loose'}`}
                            photo={photo}
                            visitDishId={visitDishId}
                            alt={alt}
                            size={72}
                          />
                        );
                        return (
                        <Card key={visit.id} className="mb-3 ms-3">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <div className="flex-grow-1">
                                <p className="text-muted mb-0 small">
                                  {new Date(visit.visit_date).toLocaleDateString()}
                                  {visit.meal_type && ` • ${visit.meal_type.charAt(0).toUpperCase() + visit.meal_type.slice(1)}`}
                                </p>
                                {/* Visited location (#63), when captured (#41). */}
                                {(visit.location_name || visit.location_city || visit.location_address) && (
                                  <p className="text-muted mb-0 small">
                                    📍 {[
                                      visit.location_name || visit.location_address,
                                      [visit.location_city, visit.location_state].filter(Boolean).join(', ')
                                    ].filter(Boolean).join(' — ')}
                                  </p>
                                )}
                              </div>
                              {/* Overall experience rating (#110), distinct from per-dish. */}
                              {visit.rating && (
                                <Badge bg="primary" title="Overall experience">★ {visit.rating}/5</Badge>
                              )}
                            </div>
                            {visit.notes && (
                              <p className="mb-2 small">{visit.notes}</p>
                            )}

                            {/* Every dish on the visit, each with its own rating +
                                per-dish photos (#118). Mirrors the grouped review card. */}
                            {dishes.length > 0 && (
                              <div className="mt-2 bg-light rounded border">
                                <div className="px-3 pt-2 pb-1 text-muted text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>
                                  {dishes.length} {dishes.length === 1 ? 'dish' : 'dishes'} on this visit
                                </div>
                                {dishes.map((d, i) => {
                                  const dishPhotos = photosByDish[d.id] || [];
                                  return (
                                    <div key={d.id} className={`px-3 py-2 ${i > 0 ? 'border-top' : ''}`}>
                                      <div className="d-flex align-items-center flex-wrap gap-2">
                                        <span className="fw-semibold small">{dishLabel(d)}</span>
                                        {d.rating ? <Badge bg="primary" className="small">★ {d.rating}/5</Badge> : null}
                                      </div>
                                      {dishPhotos.length > 0 && (
                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                          {dishPhotos.map((photo) => thumb(photo, dishLabel(d), d.id))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Public photos not tied to any dish (#91 / #115 "other"). */}
                            {loosePhotos.length > 0 && (
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {loosePhotos.map((photo) => thumb(photo, 'Visit'))}
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                        );
                      })}
                    </div>
                  ));
                })()
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">No public visits yet</p>
                </div>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default UserProfilePage;
